import React, { useEffect, useState } from "react";
import { Link, useParams, useHistory, useLocation } from "react-router-dom";
import { reverse } from "named-urls";
import {
  Table,
  PageHeader,
  Space,
  Button,
  InputNumber,
  notification,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { companyCatalogSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import * as ACTIONS from "../../../../constants/actions";
import { useAuth } from "../../../../contexts/auth";

const STORAGE_KEY = 'companyCatalogDetailFilters';
const CompanyCatalog = () => {
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: companyCatalogSaga });
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    
    return {
      page: 1,
      categoryFilter: [],
      commodityFilter: [],
      testCategoryFilter: [],
    };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [categoryFilter, setCategoryFilter] = useState(initialState.categoryFilter||[]);
  const [commodityFilter, setCommodityFilter] = useState(initialState.commodityFilter||[]);
  const [testCategoryFilter, setTestCategoryFilter] = useState(initialState.testCategoryFilter||[]);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = {
      page: currentPage,
      categoryFilter,
      commodityFilter,
      testCategoryFilter,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, categoryFilter, commodityFilter, testCategoryFilter]);

  // Fetch initial data on mount
  useEffect(() => {
    dispatch(actions.fetchCompanyDetails({ id: companyId }));
    dispatch(actions.fetchCommodityCategories());
    dispatch(actions.fetchCommodity());
    dispatch(actions.fetchTestCategory());
  }, [dispatch, companyId]);

  // Fetch catalog data when filters change
  useEffect(() => {
    let query = `page=${currentPage}&limit=10`;
    categoryFilter.forEach((c) => (query += `&test.commodityCategory=${c}`));
    commodityFilter.forEach((c) => (query += `&test.commodity=${c}`));
    testCategoryFilter.forEach((c) => (query += `&test.testCategory=${c}`));
    
    dispatch(actions.fetchCatalog({ id: companyId, query }));
  }, [dispatch, companyId, currentPage, categoryFilter, commodityFilter, testCategoryFilter]);

  const companyDetails = useSelector(selectors.selectCompanyDetails) || {};
  const catalog = useSelector(selectors.selectCatalog);
  const loading = useSelector(selectors.selectLoading);
  const totalTests = useSelector(selectors.selectTestCount);

  const tableData = catalog.map((entry) => {
    const test = entry.test || {};
    return {
      key: entry._id,
      category: test.commodityCategory?.name,
      commodity: test.commodity?.name,
      testCategory: test.testCategory?.name,
      currency: entry.currency,
      price: entry.price,
      parameter: test.parameter,
      testId: test._id,
    };
  });

  const handleEditPrice = (record) => {
    const onSuccess = (test) => {
      let query = `page=${currentPage}&limit=10`;
      dispatch(actions.fetchCatalog({ id: companyId, query }));
      notification.success({
        message: `Price Got Updated For Test ${record.parameter}!!`,
      });
    };
    const onFailure = () => {
      notification.error({
        message: "Error Updating Price!",
      });
    };
    const payloadData = {
      id: companyId,
      data: {
        testId: record.testId,
        price: record.price,
      },
      onSuccess,
      onFailure,
    };

    dispatch(actions.updateTestPriceInCompanyCatalog(payloadData));
  };

  const columns = [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: "20%",
    },
    {
      title: "Commodity",
      dataIndex: "commodity",
      key: "commodity",
      width: "20%",
    },
    {
      title: "Test Type",
      dataIndex: "testCategory",
      key: "testCategory",
      width: "20%",
    },
    {
      title: "Parameter",
      dataIndex: "parameter",
      key: "parameter",
      width: "20%",
    },
    {
      title: "Price",
      dataIndex: "price",
      editable: true,
      key: "price",
      width: "10%",
      render: (value, record) => {
        return auth.permissions.indexOf(
          ACTIONS.ACCESS_EDIT_TEST_PRICE_IN_COMPANY_CATALOG
        ) !== -1 ? (
          <Space>
            {record.currency?.value}
            <InputNumber
              min={0}
              value={record.price}
              onChange={(val) => {
                record.price = val;
              }}
            />
          </Space>
        ) : (
          `${record.currency?.value} ${record.price}`
        );
      },
    },
    ...(userRole !== "support_team"
      ? [
          {
            title: "Actions",
            key: "actions",
            width: "10%",
            render: (value, record) => (
              <Space size="small">
                {auth.permissions.indexOf(
                  ACTIONS.ACCESS_EDIT_TEST_PRICE_IN_COMPANY_CATALOG
                ) !== -1 ? (
                  <Button
                    key="edit"
                    type="link"
                    onClick={() => {
                      handleEditPrice(record);
                    }}
                  >
                    Update Price
                  </Button>
                ) : (
                  ""
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const handleBack = () => {
    history.goBack();
  };

  const handleTablePagination = (pagination, filters) => {
    setCurrentPage(pagination.current || 1);
    setCategoryFilter(filters.category || []);
    setCommodityFilter(filters.commodity || []);
    setTestCategoryFilter(filters.testCategory || []);
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        onBack={handleBack}
        ghost={false}
        title="Company Catalog"
        subTitle={companyDetails.companyName}
        extra={[
          userRole !== "support_team" && (
            <Button key="1" type="primary">
              <Link
                to={reverse(routes.backOffice.companies.details.catalog.add, {
                  companyId,
                })}
              >
                Edit Catalog
              </Link>
            </Button>
          ),
        ]}
      />

      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          dataSource={tableData}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: totalTests,
            showSizeChanger: false,
            disabled: tableData.length === 0,
          }}
          onChange={handleTablePagination}
        />
      </div>
    </div>
  );
};

export default CompanyCatalog;